import { type FC, type ReactNode, useState } from 'react'
import { Spinner } from '../../../components/Spinner/Spinner'
import { NavigationSidebar } from '../../../components/NavigationWithSideBar/NavigationSidebar'
import { useZocksAuth, useZocksQuota } from '../../../components/ScopedBlock/hooks'
import { Upsell } from '../../Plan/DeltaTrial/Upsell/Upsell'
import { DELTA_TRIAL_FEATURES } from '../../Plan/DeltaTrial/DeltaTrialFeatures'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { addToast } from '../../../store/toast/toastSlice'
import { useGetAccountSchedulingSettingsQuery, useUpdateAccountSchedulingSettingsMutation } from '../../../store/scheduling/scheduling'
import { type AccountSchedulingSettings } from '../../../models/Scheduling'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import { SharedEventTypesSection } from './sections/SharedEventTypesSection'
import { PublicBookingPageSection } from './sections/PublicBookingPageSection'
import { UserControlsSection } from './sections/UserControlsSection'
import { AvailabilitySection } from './sections/AvailabilitySection'
import { EmailTemplatesSection } from './sections/EmailTemplatesSection'

export type UpdateAccountSettings = (update: Partial<AccountSchedulingSettings>) => Promise<void>

type AccountSettingsGroup = keyof AccountSchedulingSettings

const pickGroups = (settings: AccountSchedulingSettings, groups: AccountSettingsGroup[]): Partial<AccountSchedulingSettings> =>
  groups.reduce<Partial<AccountSchedulingSettings>>((picked, group) => ({ ...picked, [group]: settings[group] }), {})

const dropGroups = (edits: Partial<AccountSchedulingSettings>, groups: AccountSettingsGroup[]): Partial<AccountSchedulingSettings> => {
  const remaining = { ...edits }
  groups.forEach((group) => delete remaining[group])
  return remaining
}

const NAV_SECTIONS = [
  { id: 'event-types', label: 'Shared event types' },
  { id: 'booking-page', label: 'Public booking page' },
  { id: 'user-controls', label: 'User controls' },
  { id: 'availability', label: 'Availability' },
  { id: 'email-templates', label: 'Email templates' },
]

export const SchedulingSetupTab: FC = () => {
  const { isFetching: quotasFetching, hasQuota, quotaSuccess } = useZocksQuota()

  if (quotasFetching) {
    return (
      <div className='d-flex justify-content-center py-5'>
        <Spinner />
      </div>
    )
  }

  if (!quotaSuccess) {
    return null
  }

  if (!hasQuota('scheduling.enabled.value')) {
    return <Upsell feature={DELTA_TRIAL_FEATURES['scheduling']} />
  }

  return <SchedulingSetupTabView />
}

const SchedulingSetupTabView: FC = () => {
  const dispatch = useAppDispatch()
  const { trackingClient } = useTracking()
  const { hasScope } = useZocksAuth()
  const accountId = useAppSelector(selectSafeAuthenticatedUser).accountId
  const readOnly = !hasScope('**', 'account_config:update', ['account_config:admin'])
  const { data, isLoading } = useGetAccountSchedulingSettingsQuery({ accountId })
  const [updateAccountSettings, { isLoading: isUpdating }] = useUpdateAccountSchedulingSettingsMutation()
  const [activeSectionId, setActiveSectionId] = useState('event-types')
  const [pendingEdits, setPendingEdits] = useState<Partial<AccountSchedulingSettings>>({})

  if (isLoading || !data) {
    return (
      <div className='d-flex justify-content-center py-5'>
        <Spinner />
      </div>
    )
  }

  const settings = { ...data, ...pendingEdits }

  const handleUpdate: UpdateAccountSettings = async (update) => {
    const editedGroups = Object.keys(update) as AccountSettingsGroup[]
    setPendingEdits((current) => ({ ...current, ...update }))
    const result = await updateAccountSettings({ accountId, update })
    if ('error' in result) {
      setPendingEdits((current) => dropGroups(current, editedGroups))
      dispatch(addToast({ variant: 'error', title: 'Could not save the setting', messages: ['Please try again.'] }))
    } else {
      setPendingEdits((current) => ({ ...current, ...pickGroups(result.data, editedGroups) }))
      trackingClient.track({ eventName: TrackEvent.SCHEDULING_ACCOUNT_SETTINGS_UPDATED, eventDetails: { fields: editedGroups } })
    }
  }

  const sectionContent: Record<string, ReactNode> = {
    'event-types': <SharedEventTypesSection readOnly={readOnly} />,
    'booking-page': <PublicBookingPageSection settings={settings} readOnly={readOnly} isSaving={isUpdating} onUpdate={handleUpdate} />,
    'user-controls': <UserControlsSection settings={settings} readOnly={readOnly} isSaving={isUpdating} onUpdate={handleUpdate} />,
    availability: <AvailabilitySection settings={settings} readOnly={readOnly} isSaving={isUpdating} onUpdate={handleUpdate} />,
    'email-templates': <EmailTemplatesSection />,
  }

  return (
    <div className='d-flex flex-column gap-4'>
      {readOnly ? (
        <div className='paragraph-small zocks-text-gray-600 p-3 border rounded zocks-border-color-gray-200 zocks-bg-color-gray-100'>
          <i className='bi bi-info-circle me-1' />
          These Scheduling settings are managed by your account admins and are view-only for you.
        </div>
      ) : null}
      <div className='scroll-navigation__container'>
        <div className='d-flex flex-column gap-4'>
          <NavigationSidebar sections={NAV_SECTIONS} activeSectionId={activeSectionId} onSectionClick={setActiveSectionId} />
        </div>
        <div className='scroll-navigation__sections'>{sectionContent[activeSectionId]}</div>
      </div>
    </div>
  )
}
